import { Modal, ModalBody, ModalContent, ModalOverlay, useToast } from "@chakra-ui/react";
import { AxiosResponse } from "axios";
import { Form, Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Button } from "../../components/ui/button";
import { TextArea } from "../../components/ui/form";
import Workspace from "../../components/workspace";
import authService from "../../services/auth-service";
import { ReportComment } from "./report-comment";
import { serviceReportService } from "./service-report.service";

const ServiceReportComments = ({ modal, serviceReport, setSelectedReport }) => {
   const [reportComments, setReportComments] = useState([]);
   const { id: currentUserId, firstName, lastName } = authService.getUserInfo();
   const [isLoading, setIsLoading] = useState(true);
   const [isDeleting, setIsDeleting] = useState(false);
   const [fetchError, setFetchError] = useState("");
   const [submitError, setSubmitError] = useState("");
   const heading = "Report Comments";
   const controller = new AbortController();
   const toast = useToast();

   const serviceReportId = useMemo(() => serviceReport.id, [serviceReport.id]);

   useEffect(() => {
      const loadComments = async () => {
         try {
            const fetchResult = await serviceReportService.getReportComments(serviceReportId);
            setReportComments(fetchResult.data);
         } catch (error) {
            setFetchError(error.message);
         } finally {
            setIsLoading(false);
         }
      };
      serviceReportId && loadComments();

      return () => {
         setFetchError("");
         setIsLoading(true);
         setSubmitError("");
      };
   }, [serviceReportId]);

   const submitComment = async (comment, { resetForm }) => {
      setSubmitError(null);

      try {
         const submitResult = (await serviceReportService.submitReportComment({
            comment,
            reportId: serviceReport.id,
            id: comment.id,
            editOrCreate: comment.id ? "edit" : "create",
         })) as AxiosResponse & { message: string };
         toast({ description: submitResult.message, status: "success", title: "Create comment" });

         const newCommentWithId = Object.assign(
            {},
            comment,
            { id: submitResult.data.id, createdAt: new Date().toISOString() },
            { user: { id: currentUserId, firstName, lastName } }
         );

         setReportComments((_prev) => [..._prev, newCommentWithId]);
         resetForm();
      } catch (error) {
         setSubmitError(error.message);
      }
   };

   const reset = () => {
      setReportComments([]);
      setSubmitError("");
      setSelectedReport({});
   };

   const deleteComment = async (id) => {
      setIsDeleting(true);
      try {
         const result = (await serviceReportService.deleteReportComment({
            reportId: serviceReport.id,
            id,
         })) as AxiosResponse & { message: string };
         setReportComments((prev) =>
            prev.map((_comment) => (_comment.id === id ? Object.assign({}, _comment, { deletedAt: Date.now() }) : _comment))
         );
         toast({ description: result.message, status: "success", title: "Delete comment" });
      } catch (error) {
         setSubmitError(error.message);
      } finally {
         setIsDeleting(false);
      }
   };

   const validateComment = (values) => {};

   if (isLoading) return <Workspace.LoadingModal controller={controller} heading={heading} modal={modal} />;
   if (fetchError) return <Workspace.FetchErrorModal modal={modal} heading={heading} message={fetchError} callback={reset} />;

   return (
      <Modal
         isOpen={modal.isOpen}
         onClose={() => {
            reset();
            modal.onClose();
         }}
         size="2xl">
         <ModalOverlay />
         <ModalContent className="!rounded-2xl">
            <Workspace.ModalHeader>
               <Workspace.ModalHeaderWithCloseButton>{heading}</Workspace.ModalHeaderWithCloseButton>
            </Workspace.ModalHeader>
            <ModalBody className="grid">
               <div className="grid gap-y-10 py-5 px-4">
                  <ServiceReportDetails report={serviceReport} />
                  <div className="grid gap-y-4 border-b border-gray-300">
                     <h2 className="font-semibold">Comments:</h2>
                     <div className="grid gap-y-4 pb-8 max-h-[80%]">
                        {Boolean(reportComments.length) ? (
                           reportComments.map((_comment) => (
                              <ReportComment
                                 key={_comment.id}
                                 comment={_comment}
                                 isSelfComment={_comment.user.id === currentUserId}
                                 deleteComment={deleteComment}
                                 isDeleting={isDeleting}
                              />
                           ))
                        ) : (
                           <p className="text-center text-gray-600 italic my-8 text-sm">Comments would be displayed here.</p>
                        )}
                     </div>
                  </div>

                  <div className="comment-form | ">
                     <Formik
                        initialValues={{ content: "", serviceReportId: "" }}
                        validate={validateComment}
                        onSubmit={submitComment}
                        enableReinitialize>
                        {({ isSubmitting }) => (
                           <Form className="grid gap-4">
                              <div className="grid gap-1">
                                 <TextArea
                                    className="resize-none"
                                    name="content"
                                    placeholder="Add comment..."
                                    rows={5}
                                    wrap="hard"
                                 />
                                 {Boolean(submitError) ? (
                                    <Workspace.FormSubmitError>{submitError}</Workspace.FormSubmitError>
                                 ) : null}
                              </div>
                              <div className="mb-3 flex justify-end gap-2">
                                 <Button
                                    type="reset"
                                    variant="secondary"
                                    onClick={() => {
                                       controller.abort();
                                    }}>
                                    Cancel
                                 </Button>
                                 <Button variant="primary" type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                    Post
                                 </Button>
                              </div>
                           </Form>
                        )}
                     </Formik>
                  </div>
               </div>
            </ModalBody>
         </ModalContent>
      </Modal>
   );
};

export { ServiceReportComments };

const ServiceReportDetails = ({ report }) => {
   return (
      <div className="report-summary | overflow-hidden my-3 md:w-2/3 bg-gray-100 rounded-2xl text-xs">
         <EntityDetailsCapsule label={"Branch Code"} value={report.branchCode} />
         <EntityDetailsCapsule label={"Service Name"} value={report.name} />
         <EntityDetailsCapsule label={"Sermon Title"} value={report.sermonTitle} />
         <EntityDetailsCapsule label={"Service Date"} value={report.serviceDate} />
         <EntityDetailsCapsule label={"Submission Date"} value={report.createdAt.substring(0, 10)} />
      </div>
   );
};

const EntityDetailsCapsule = ({ label, value }) => {
   return (
      <p className="last:border-0 border-b border-b-gray-300">
         <span className="bg-gray-200 py-2 px-3 text-right font-semibold w-1/2 md:w-1/3 inline-block">{label}:</span>
         <span className="px-2">{value}</span>
      </p>
   );
};
